import { Overlay } from '../patterns/overlay'
import { Focusable } from '../../accessibility/focusable'
import { stopBodyScroll, allowBodyScroll } from '../controls/controlBodyScroll'
import { setupHoverIntent } from '../../utilities/HoverIntent'
import { isDesktop, isLargeTouchDevice } from '../../utilities/MediaQueries.js'

const openClass = 'hdr-bar__link-open'
const menuOpenClass = 'offcanvas--sub-menus-open'

// A class to handle the opening and closing of off canvas elements via flyout menu.
// Hover intent is used to trigger dialog opening.
export class FlyoutDelegates {
  constructor (elem) {
    this.elem = elem

    this.togglers = this.elem.querySelectorAll('[data-flyout-toggle="true"]')
    this.toggleElements = []
    this.lastClicked = {}
    this.header = document.getElementById('Header')

    this.mainMenu = document.getElementById('leshuttle-menu')
    this.overlay = new Overlay('overlay--desktop-mm')

    this.mainMenu.addEventListener('mouseleave', (e) => {
      if (isDesktop()) {
        this.timer = setTimeout(() => {
          this.closeOtherDelegates('ALL')
          this.closeMenu(false)
        }, 100)
      }
    })

    for (const toggler of this.togglers) {
      const toggleElem = {}
      toggleElem.elem = toggler
      toggleElem.open = false
      toggleElem.controlId = toggler.getAttribute('aria-controls')
      toggleElem.target = document.getElementById(toggleElem.controlId)
      toggleElem.menu = toggleElem.target.querySelector('.sub-menu')
      toggleElem.summary = toggleElem.target.querySelector('summary')
      toggleElem.focusState = new Focusable(toggleElem.menu, true)
      toggleElem.focusState.onEscape(() => this.closeMenu())
      this.toggleElements.push(toggleElem)

      toggler.addEventListener('click', (e) => {
        if (isLargeTouchDevice()) {
          e.preventDefault()
          e.stopPropagation()
        }
      })
    }

    for (const toggler of this.toggleElements) {
      toggler.elem.addEventListener('keydown', (e) => {
        clearTimeout(this.timer)
        if (e.keyCode === 13) {
          e.preventDefault()
          e.stopPropagation()
          this.toggle(toggler)
          setTimeout(() => {
            toggler.focusState.focusFirst()
          }, 100)
        }
      })

      const elems = []
      elems.push(toggler.elem)

      setupHoverIntent(elems, {
        // required parameters
        onEnter: (targetItem) => {
          this.openByButton(targetItem)
        },
        onExit: (targetItem, event) => {
          // Create timeout that can be cancelled to close the menu.
          this.closeByButton(targetItem, event)
        },
        exitDelay: 400,
        interval: 100,
        sensitivity: 7
      })

      /* toggler.elem.addEventListener('mouseover', (e) => {
        if (toggler.open === false) {
          this.mainMenu.style.top = `${this.header.getBoundingClientRect().bottom}px`
          this.overlay.elem.style.top = `${this.header.getBoundingClientRect().bottom}px`
          e.preventDefault()
          e.stopPropagation()
          this.toggle(toggler)
        }
      }) */
    }
  }

  mainMenuOpen () {
    return this.mainMenu.classList.contains(menuOpenClass)
  }

  openMenu () {
    requestAnimationFrame(() => {
      stopBodyScroll()
      this.mainMenu.classList.add(menuOpenClass)
      this.overlay.open()
      this.mainMenu.setAttribute('aria-hidden', 'false')
    })
  }

  closeMenu (refocus = true) {
    requestAnimationFrame(() => {
      this.allowScrollTimer = setTimeout(() => {
        allowBodyScroll()
      }, 300)
      this.mainMenu.classList.remove(menuOpenClass)
      this.overlay.close()
      this.mainMenu.setAttribute('aria-hidden', 'true')
      if (refocus && typeof (this.lastClicked.elem) !== 'undefined') {
        this.lastClicked.elem.focus()
      }
    })
  }

  toggleOpen (toggler) {
    if (toggler.open === false) {
      this.mainMenu.style.top = `${this.header.getBoundingClientRect().bottom}px`
      this.overlay.elem.style.top = `${this.header.getBoundingClientRect().bottom}px`
      toggler.preventDefault()
      toggler.stopPropagation()
      this.toggle(toggler)
    }
  }

  delegateOpen (toggler) {
    return toggler.target.hasAttribute('open')
  }

  openDelegate (toggler) {
    toggler.open = true
    this.lastClicked = toggler
    toggler.elem.classList.add(openClass)
  }

  closeDelegate (toggler) {
    toggler.open = false
    toggler.elem.classList.remove(openClass)
    toggler.target.removeAttribute('open')
  }

  closeOtherDelegates (toggler) {
    for (const everyToggler of this.toggleElements) {
      if (everyToggler !== toggler && everyToggler.open) {
        this.closeDelegate(everyToggler)
      }
    }
  }

  openByButton (toggleButton) {
    stopBodyScroll()
    clearTimeout(this.timer)
    clearTimeout(this.allowScrollTimer)
    const activeElem = toggleButton.getAttribute('aria-controls')
    const toggler = this.toggleElements.find(tgl => tgl.controlId === activeElem)
    if (toggler.open === false) {
      this.mainMenu.style.top = `${this.header.getBoundingClientRect().bottom}px`
      this.overlay.elem.style.top = `${this.header.getBoundingClientRect().bottom}px`
      this.toggle(toggler)
    }
  }

  closeByButton (toggleButton, event) {
    const activeElem = toggleButton.getAttribute('aria-controls')
    const toggler = this.toggleElements.find(tgl => tgl.controlId === activeElem)
    if (toggler.open) {
      if (!this.isMouseOverElement(event)) {
        toggler.open = false
        toggler.elem.classList.remove(openClass)
        toggler.target.removeAttribute('open')
        this.closeMenu(false)
      }
    }
  }

  toggle (toggler) {
    // is this menu open?
    this.closeOtherDelegates(toggler)
    if (this.delegateOpen(toggler)) {
      this.closeMenu()
      this.closeDelegate(toggler)
      toggler.summary.click()
    } else {
      this.openMenu()

      if (this.delegateOpen(toggler) === false) {
        this.openDelegate(toggler)
        toggler.summary.click()
      }
    }
  }

  isMouseOverElement (event) {
    // Get a reference to the element by its ID
    const element = document.getElementById('leshuttle-menu')

    if (!element) {
      // Element with the specified ID does not exist
      return false
    }
    // Get the position of the element's bounding box
    const elementRect = element.getBoundingClientRect()

    // Get the mouse's current position from the event object
    const mouseX = event.clientX
    const mouseY = event.clientY

    // Check if the mouse is within the element's bounding box
    if (
      mouseX >= elementRect.left &&
      mouseX <= elementRect.right &&
      mouseY >= (elementRect.top - 10) &&
      mouseY <= (elementRect.bottom + 10)
    ) {
      // Mouse is over the element
      return true
    }

    // Mouse is not over the element
    return false
  }
}

export function loadFlyoutDelegates () {
  const elems = document.querySelectorAll('[data-flyout-togglers="true"]')
  const flyoutDelegates = []

  for (const element of elems) {
    flyoutDelegates.push(new FlyoutDelegates(element))
    // setupHoverIntent(elems, )
  }

  return flyoutDelegates
}
